import React, { useEffect, useState, useMemo } from "react";
import api, { money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["New Request", "Awaiting Contractor", "Contractor Assigned", "Scheduled", "Estimate Needed", "Awaiting Client Approval", "In Progress", "Completed", "Invoice Pending", "Paid", "Cancelled"];
const URGENCIES = ["Routine", "Priority", "Urgent", "Emergency"];
const BLANK_PROP = { name: "", street_address: "", city: "", state: "", zip_code: "", property_type: "Residential" };
const BLANK_FORM = {
  client_id: "", property_id: "", service_category: "", job_title: "", problem_description: "",
  urgency: "Routine", customer_price: "", contractor_payout: "", material_cost: "",
  requested_date: "", scheduled_date: "", scheduled_time: "", due_date: "",
  contractor_id: "unassigned", status: "Awaiting Contractor", internal_notes: "", newprop: { ...BLANK_PROP },
};

export function CreateWorkOrderDialog({ open, onOpenChange, onCreated }) {
  const [busy, setBusy] = useState(false);
  const [clients, setClients] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [props, setProps] = useState([]);
  const [clientQuery, setClientQuery] = useState("");
  const [newProp, setNewProp] = useState(false);
  const [f, setF] = useState({ ...BLANK_FORM });

  useEffect(() => {
    if (open) {
      api.get("/admin/clients").then(({ data }) => setClients(data)).catch(() => {});
      api.get("/contractors").then(({ data }) => setContractors(data)).catch(() => setContractors([]));
    }
  }, [open]);

  useEffect(() => {
    if (f.client_id) {
      api.get(`/properties?client_id=${f.client_id}`).then(({ data }) => setProps(data)).catch(() => setProps([]));
      setF((s) => ({ ...s, property_id: "" }));
    } else setProps([]);
    // eslint-disable-next-line
  }, [f.client_id]);

  const filteredClients = useMemo(
    () => clients.filter((c) => (c.name + c.email).toLowerCase().includes(clientQuery.toLowerCase())),
    [clients, clientQuery]
  );
  const gp = (Number(f.customer_price) || 0) - (Number(f.contractor_payout) || 0) - (Number(f.material_cost) || 0);
  const gm = Number(f.customer_price) ? (gp / Number(f.customer_price)) * 100 : 0;
  const set = (k) => (e) => setF({ ...f, [k]: e.target?.value ?? e });

  const reset = () => { setF({ ...BLANK_FORM }); setNewProp(false); setClientQuery(""); };

  const submit = async () => {
    if (!f.client_id) return toast.error("Select a client");
    if (!f.service_category || !f.problem_description) return toast.error("Trade/Service and Description are required");
    let property_id = f.property_id;
    setBusy(true);
    try {
      if (newProp || !property_id) {
        const np = f.newprop;
        if (!np.name || !np.street_address || !np.city || !np.state || !np.zip_code) {
          setBusy(false); return toast.error("Fill all new property fields");
        }
        const { data } = await api.post("/properties", { ...np, client_id: f.client_id });
        property_id = data.id;
      }
      const payload = {
        property_id, service_category: f.service_category, job_title: f.job_title,
        problem_description: f.problem_description, urgency: f.urgency, status: f.status,
        requested_date: f.requested_date, scheduled_date: f.scheduled_date, scheduled_time: f.scheduled_time,
        due_date: f.due_date, internal_notes: f.internal_notes,
        customer_price: Number(f.customer_price) || 0, contractor_payout: Number(f.contractor_payout) || 0,
        material_cost: Number(f.material_cost) || 0,
        contractor_id: f.contractor_id === "unassigned" ? null : f.contractor_id,
      };
      await api.post("/work-orders", payload);
      toast.success("Work order created successfully.");
      onOpenChange(false);
      reset();
      onCreated && onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create work order"); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="create-wo-modal">
        <DialogHeader><DialogTitle>Create Work Order</DialogTitle><DialogDescription>Select a client and property, then set service details and pricing.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Client</Label>
            <Input data-testid="wo-client-search" value={clientQuery} onChange={(e) => setClientQuery(e.target.value)} placeholder="Search clients…" className="mt-1.5 mb-2" />
            <Select value={f.client_id} onValueChange={(v) => setF({ ...f, client_id: v })}>
              <SelectTrigger data-testid="wo-client-select"><SelectValue placeholder="Select a client" /></SelectTrigger>
              <SelectContent>{filteredClients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {f.client_id && (
            <div>
              <div className="flex items-center justify-between"><Label>Property</Label>
                <button type="button" data-testid="wo-toggle-newprop" className="text-xs text-blue-600" onClick={() => setNewProp(!newProp)}>{newProp ? "Choose existing" : "+ Add New Property"}</button></div>
              {!newProp ? (
                <Select value={f.property_id} onValueChange={(v) => setF({ ...f, property_id: v })}>
                  <SelectTrigger data-testid="wo-property-select" className="mt-1.5"><SelectValue placeholder={props.length ? "Select property" : "No properties — add new"} /></SelectTrigger>
                  <SelectContent>{props.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} — {p.street_address}</SelectItem>)}</SelectContent>
                </Select>
              ) : (
                <div className="grid grid-cols-2 gap-2 mt-1.5">
                  <Input data-testid="wo-np-name" placeholder="Property name" value={f.newprop.name} onChange={(e) => setF({ ...f, newprop: { ...f.newprop, name: e.target.value } })} />
                  <Input data-testid="wo-np-street" placeholder="Street address" value={f.newprop.street_address} onChange={(e) => setF({ ...f, newprop: { ...f.newprop, street_address: e.target.value } })} />
                  <Input data-testid="wo-np-city" placeholder="City" value={f.newprop.city} onChange={(e) => setF({ ...f, newprop: { ...f.newprop, city: e.target.value } })} />
                  <Input data-testid="wo-np-state" placeholder="State" value={f.newprop.state} onChange={(e) => setF({ ...f, newprop: { ...f.newprop, state: e.target.value } })} />
                  <Input data-testid="wo-np-zip" placeholder="ZIP code" value={f.newprop.zip_code} onChange={(e) => setF({ ...f, newprop: { ...f.newprop, zip_code: e.target.value } })} />
                </div>
              )}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Trade / Service</Label><Input data-testid="wo-service" value={f.service_category} onChange={set("service_category")} className="mt-1.5" placeholder="Plumbing, HVAC…" /></div>
            <div><Label>Job Title</Label><Input data-testid="wo-title" value={f.job_title} onChange={set("job_title")} className="mt-1.5" /></div>
          </div>
          <div><Label>Description</Label><Textarea data-testid="wo-desc" value={f.problem_description} onChange={set("problem_description")} className="mt-1.5" rows={2} /></div>
          <div><Label>Urgency</Label>
            <Select value={f.urgency} onValueChange={(v) => setF({ ...f, urgency: v })}><SelectTrigger data-testid="wo-urgency" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>{URGENCIES.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent></Select></div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Client Price</Label><Input data-testid="wo-price" type="number" value={f.customer_price} onChange={set("customer_price")} className="mt-1.5" /></div>
            <div><Label>Contractor Cost</Label><Input data-testid="wo-payout" type="number" value={f.contractor_payout} onChange={set("contractor_payout")} className="mt-1.5" /></div>
            <div><Label>Material Cost</Label><Input data-testid="wo-material" type="number" value={f.material_cost} onChange={set("material_cost")} className="mt-1.5" /></div>
          </div>
          <div className="text-sm text-slate-600 bg-slate-50 rounded-lg px-3 py-2" data-testid="wo-gp">Est. Gross Profit: <b>{money(gp)}</b> · Margin: <b>{gm.toFixed(1)}%</b></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Requested Date</Label><Input data-testid="wo-requested" type="date" value={f.requested_date} onChange={set("requested_date")} className="mt-1.5" /></div>
            <div><Label>Scheduled Date</Label><Input data-testid="wo-scheduled" type="date" value={f.scheduled_date} onChange={set("scheduled_date")} className="mt-1.5" /></div>
            <div><Label>Scheduled Time</Label><Input data-testid="wo-time" type="time" value={f.scheduled_time} onChange={set("scheduled_time")} className="mt-1.5" /></div>
            <div><Label>Due Date</Label><Input data-testid="wo-due" type="date" value={f.due_date} onChange={set("due_date")} className="mt-1.5" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Contractor</Label>
              <Select value={f.contractor_id} onValueChange={(v) => setF({ ...f, contractor_id: v })}><SelectTrigger data-testid="wo-contractor" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="unassigned">Unassigned</SelectItem>{contractors.map((c) => <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Status</Label>
              <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}><SelectTrigger data-testid="wo-status" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
          </div>
          <div><Label>Internal Notes</Label><Textarea data-testid="wo-notes" value={f.internal_notes} onChange={set("internal_notes")} className="mt-1.5" rows={2} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="wo-submit" onClick={submit} disabled={busy} className="bg-blue-600 hover:bg-blue-700">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Work Order"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
